import { motion, useScroll, useTransform } from "framer-motion";
import { ChevronDown, Sparkles } from "lucide-react";
import { BANNER_URL } from "@/data/books";
import { scrollToSection } from "@/lib/scroll";

const EASE = [0.16, 1, 0.3, 1];

const MaskedLine = ({ children, delay }) => (
  <span className="block overflow-hidden">
    <motion.span
      className="block"
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.1, ease: EASE, delay }}
    >
      {children}
    </motion.span>
  </span>
);

export default function Hero() {
  const { scrollY } = useScroll();
  const bgY = useTransform(scrollY, [0, 900], [0, 260]);
  const fade = useTransform(scrollY, [0, 600], [1, 0]);

  return (
    <section data-testid="hero-section" className="relative flex min-h-screen items-end overflow-hidden">
      <motion.div style={{ y: bgY }} className="absolute inset-0 scale-110">
        <img
          src={BANNER_URL}
          alt="Fr. J. Stopa in a candlelit abbey library"
          className="h-full w-full object-cover object-center"
          data-testid="hero-banner-image"
        />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-t from-[#16110D] via-[#16110D]/45 to-[#16110D]/30" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_35%,rgba(22,17,13,0.55)_100%)]" />

      <motion.div
        style={{ opacity: fade }}
        className="relative z-10 mx-auto w-full max-w-7xl px-6 pb-24 pt-40 md:px-12 md:pb-32"
      >
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: EASE, delay: 0.2 }}
          className="mb-6 flex items-center gap-3"
        >
          <Sparkles className="h-4 w-4 text-abbey-gold" strokeWidth={1.5} />
          <p className="font-cinzel text-[11px] tracking-[0.4em] text-abbey-gold md:text-xs">
            THE BOOKS OF FR. J. STOPA
          </p>
        </motion.div>

        <h1 className="font-cormorant text-5xl font-medium leading-[1.02] tracking-tight text-abbey-cream sm:text-6xl lg:text-8xl">
          <MaskedLine delay={0.35}>Stories of Faith,</MaskedLine>
          <MaskedLine delay={0.5}>
            <span className="italic text-abbey-gold">Wonder</span> &amp; Grace.
          </MaskedLine>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: EASE, delay: 0.75 }}
          className="mt-8 max-w-xl text-sm leading-relaxed text-abbey-cream/75 md:text-base"
        >
          Five books from theologian and storyteller J. Stopa — exclusively on
          Amazon KDP, and most also available on Audible.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: EASE, delay: 0.9 }}
          className="mt-12 flex flex-wrap items-center gap-5"
        >
          <button
            data-testid="hero-browse-books-btn"
            onClick={() => scrollToSection("books")}
            className="border border-abbey-gold bg-abbey-gold px-9 py-4 font-cinzel text-[11px] font-semibold tracking-[0.3em] text-abbey-dark transition-colors duration-300 hover:bg-transparent hover:text-abbey-gold"
          >
            BROWSE THE SHELF
          </button>
          <button
            data-testid="hero-meet-author-btn"
            onClick={() => scrollToSection("about")}
            className="border border-abbey-cream/30 bg-transparent px-9 py-4 font-cinzel text-[11px] font-semibold tracking-[0.3em] text-abbey-cream transition-colors duration-300 hover:border-abbey-gold hover:text-abbey-gold"
          >
            MEET THE AUTHOR
          </button>
        </motion.div>
      </motion.div>

      <motion.button
        data-testid="hero-scroll-indicator"
        onClick={() => scrollToSection("books")}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1 }}
        className="absolute bottom-6 left-1/2 z-10 -translate-x-1/2 text-abbey-gold/70 transition-colors duration-300 hover:text-abbey-gold"
        aria-label="Scroll to books"
      >
        <ChevronDown className="h-6 w-6 animate-bounce" strokeWidth={1.5} />
      </motion.button>
    </section>
  );
}
